/**
 * @file Firestore CRUD for the application-side `users/{uid}` documents. These are the
 * profile/preferences records that complement the Firebase Auth user (email, displayName,
 * preferredLanguage, disclaimer acceptance, category blacklist, etc).
 *
 * Most exports are thin partial-update helpers around a single field — the contract is
 * "atomically set this one preference"; they all surface via `Parameters.vue` and the
 * auth/disclaimer flows.
 */

import { getDoc, doc, updateDoc, type DocumentData } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/main'
import type { User } from '@/types/Types'
import { ALL_AVATARS } from '@/types/Constants'
import { deleteImageFromBucket } from './methods'

/**
 * Centralised partial-update helper. Wraps `updateDoc` so every public mutator below shares
 * the same "User update failed for {uid}: {message}" error shape — the toast layer can
 * surface one consistent message regardless of which field failed.
 */
async function updateUserField(userId: string, fields: DocumentData): Promise<void> {
  try {
    await updateDoc(doc(db, 'users', userId), fields)
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to update user'
    throw new Error(`User update failed for ${userId}: ${message}`)
  }
}

/**
 * Fetches the Firestore `User` document and stamps the document id onto the returned object.
 * Used by `useAuthStore` to hydrate `dbUser` immediately after the Firebase Auth user resolves.
 */
export async function getAdditionnalUserInfo(userID: string): Promise<User> {
  const updatedUser = (await getDoc(doc(db, 'users', userID))).data() as User
  updatedUser.id = userID
  return updatedUser
}

/** Sets `currentGameId` so the "Continue game" button on the main menu knows which game to resume. */
export async function assignGameToUser(userId: string, gameId: string): Promise<void> {
  await updateUserField(userId, { currentGameId: gameId })
}

/**
 * Updates the user's profile photo URL. If `previousImage` is provided, the old Storage
 * blob is deleted first so profile-photo replacements do not orphan files in the bucket.
 */
export async function changeUserImage(
  userId: string,
  image: string,
  previousImage: string | undefined = ''
): Promise<void> {
  if (previousImage) {
    await deleteImageFromBucket(previousImage)
  }
  await updateUserField(userId, { photoURL: image })
}

/** Mirrors the Firebase Auth email change into the Firestore profile so server-side queries can use it. Called after `authService.updateEmail` succeeds. */
export async function changeUserEmail(userId: string, email: string): Promise<void> {
  await updateUserField(userId, { email })
}

/** Sets the display name used across the UI (navbar, scorecard host label, etc). */
export async function changeUserDisplayName(userId: string, displayName: string): Promise<void> {
  await updateUserField(userId, { displayName })
}

/** Toggles marketing-email opt-in. The Cloud Functions email scheduler reads this flag. */
export async function toggleEmailList(userId: string, emailList: boolean): Promise<void> {
  await updateUserField(userId, { emailList })
}

/** Persists the user's preferred vue-i18n locale so the next session restores it before mount. */
export async function changeUserLanguage(userId: string, language: string): Promise<void> {
  await updateUserField(userId, { preferredLanguage: language })
}

/** Records that the user accepted the in-game disclaimer, plus the timestamp. Gates entry into the game flow. */
export async function acceptDisclaimer(userId: string): Promise<void> {
  await updateUserField(userId, {
    disclaimerAccepted: true,
    disclaimerAcceptedDate: new Date()
  })
}

/**
 * Stores the user's blacklisted challenge categories — the pregame challenge sampler
 * uses this list to skip challenges the user does not want. The UI caps the blacklist
 * at 2 entries (see `Parameters.vue`); this helper does not enforce that cap.
 */
export async function updateCategoryBlacklist(userId: string, blacklist: string[]): Promise<void> {
  await updateUserField(userId, { categoryBlacklist: blacklist })
}

/**
 * Persists the user's challenge blacklist via the `writeChallengeBlacklist` Cloud Function.
 *
 * WHY a callable instead of `updateDoc`: Firestore security rules block direct client writes
 * to `users/{uid}.challengeBlacklist` (backend ADR-0002). The server is the authoritative
 * source for cap enforcement and challenge-existence validation — all blacklist mutations
 * must go through it.
 */
export async function updateChallengeBlacklist(
  _userId: string,
  blacklist: string[]
): Promise<{ challengeBlacklist: string[]; cap: number; totalChallengeCount: number }> {
  const callable = httpsCallable<
    { challengeBlacklist: string[] },
    { challengeBlacklist: string[]; cap: number; totalChallengeCount: number }
  >(functions, 'writeChallengeBlacklist')
  const result = await callable({ challengeBlacklist: blacklist })
  return result.data
}

/**
 * Returns the static `ALL_AVATARS` roster. All avatars are available to every user, so
 * this is effectively a constant accessor — wrapped in a function for future-proofing
 * if avatars ever become user-scoped (unlocks, packs).
 */
export function checkAvailableAvatars() {
  return ALL_AVATARS
}
